import React, { useEffect, useState } from "react";
import axios from "axios";
import { UserCheck, MessagesSquare, Accessibility, HeartHandshake } from "lucide-react";
import { PageHeader, Section, Card, Prose, PlaceholderImage, ButtonLink } from "../components/ui-kit";
import { LIFE_SKILLS_PHOTOS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (src) => (src?.startsWith("http") ? src : `${BACKEND}${src}`);

const PILLARS = [
  { icon: UserCheck, title: "Person-centred planning", text: "We start with the person, listening to what matters to them and setting goals together." },
  { icon: Accessibility, title: "Accessible support", text: "We adapt our communication, environment and activities to suit sensory, physical, cognitive and learning needs." },
  { icon: MessagesSquare, title: "Clear communication", text: "We use the communication methods that work best for each person, and keep families informed." },
  { icon: HeartHandshake, title: "Working in partnership", text: "We work alongside families, carers, social workers and referral partners as a team. We can also support the person in finding paid or non paid work by working with local businesses." },
];

export default function HowWeSupport() {
  const [photos, setPhotos] = useState(LIFE_SKILLS_PHOTOS);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((r) => {
        if (r.data["life-skills"]?.length) setPhotos(r.data["life-skills"]);
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="How we support people"
        title="Support shaped around each person"
        intro="Good support is personal. We take time to understand each person and adapt how we work so everyone can take part, feel safe and make progress."
        testid="how-we-support-header"
      />
      <Section tone="default">
        <div className="grid gap-8 sm:grid-cols-2">
          {PILLARS.map((p) => (
            <Card key={p.title} data-testid={`support-pillar-${p.title.split(" ")[0].toLowerCase()}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
                <p.icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h2 className="mt-5 font-heading text-xl font-semibold text-stone-900">{p.title}</h2>
              <p className="mt-2 text-lg text-stone-700 leading-relaxed">{p.text}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="white">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <PlaceholderImage
            src="https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/hz4qbkr1_IMG-20260611-WA0003.jpg"
            alt="Two people we support enjoying an interactive sensory light floor game and sharing a high five."
          />
          <Prose>
            <h2 className="font-heading text-3xl font-semibold text-stone-900">A typical first step</h2>
            <p>
              We usually begin with a friendly conversation to understand what a person enjoys, what
              they would like to work towards, and how we can support them best. Families and carers
              are welcome to be part of this.
            </p>
            <p>
              From there, we build a flexible plan and review it regularly, adjusting as the person
              grows in confidence and their goals change.
            </p>
          </Prose>
        </div>
      </Section>

      <Section tone="default" data-testid="life-skills-gallery">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">
            Building life skills in the community
          </h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            Real independence is built through everyday experiences. From cooking and baking to
            eating out, ordering and managing money, the people we support practise meaningful skills
            in real-life settings.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {photos.map((p, i) => (
            <figure
              key={p.id || i}
              className="overflow-hidden rounded-3xl bg-stone-200 border border-stone-200 aspect-[4/3]"
              data-testid={`life-skills-photo-${i}`}
            >
              <img
                src={resolveSrc(p.src)}
                alt={p.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </figure>
          ))}
        </div>
      </Section>

      <Section tone="tinted">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">Referrals and enquiries</h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            Thinking about whether Spire Inclusion is the right fit? We're always happy to talk
            things through, with no pressure.
          </p>
          <p className="mt-3 text-lg text-stone-700 leading-relaxed">
            We welcome referrals from social workers and commissioners, as well as from families and
            people making a self-referral. To make a referral or ask about availability, just contact
            us by phone or email and we'll arrange a visit and a relaxed conversation to understand
            the person's needs and goals. We can support placements funded by a local authority or
            the NHS, and through personal budgets or direct payments.
          </p>
          <ButtonLink to="/contact" className="mt-8" data-testid="how-we-support-contact-button">
            Start an enquiry
          </ButtonLink>
        </div>
      </Section>
    </>
  );
}
